import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { find, some, uniqueId } from 'lodash-es';
import { Subject } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { RktTableSearchComponent } from '@rocketfinancialcorp/rocket-ui/table';

import { BusinessAccountListComponent } from '@shared/components';
import { LinkedBusinessAccount, NavigationMenuItem } from '@shared/models';
import { BRAND_CONFIG } from '@shared/services';
import { AuthActions, fromAuth } from '@shared/store';

// Relative, not via '@shared/components': that barrel re-exports this component, and the resulting
// cycle leaves AppVersionComponent undefined when the decorator runs.
import { AppVersionComponent } from '../app-version/app-version.component';

@Component({
  selector: 'app-sidebar',
  templateUrl: 'sidebar.component.html',
  styleUrls: ['sidebar.component.scss'],
  imports: [
    RouterLink,
    NgTemplateOutlet,
    AsyncPipe,
    RktIconComponent,
    RktTableSearchComponent,
    NgSelectModule,
    FormsModule,
    NgClass,
    AppVersionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class SidebarComponent implements AfterViewChecked, AfterViewInit, OnDestroy {
  @Input() menuItems: NavigationMenuItem[] = [];
  @Input() developerMenuItems: NavigationMenuItem[] = [];

  brand = inject(BRAND_CONFIG);

  businessAccountDropdownId = uniqueId();

  isSponsorTenant$ = this.store.select(fromAuth.selectSponsorTenant).pipe(take(1));

  selectedBusinessAccountId?: string;

  selectedBusinessAccount$ = this.store.select(fromAuth.selectBusinessAccountDetails).pipe(
    tap({
      next: (businessAccount) => {
        if (businessAccount) {
          this.selectedBusinessAccountId = businessAccount.id;
        }
      },
    }),
  );

  businessAccounts$ = this.store.select(fromAuth.selectLinkedBusinessAccounts);

  businessAccountsLoading$ = this.store.select(fromAuth.selectLinkedBusinessAccountsLoading);

  private destroy$ = new Subject<void>();

  constructor(
    public ref: ChangeDetectorRef,
    private router: Router,
    private store: Store,
    private modalService: ModalService,
  ) {}

  ngAfterViewInit(): void {
    const menuItem = find(this.menuItems, (item) => {
      return (item.href && this.isActive(item.href)) || this.hasActiveSubItem(item);
    });
    if (!menuItem) return;
    menuItem.isCollapsed = false;
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isActive(menuHref: string): boolean {
    return this.router.isActive(menuHref, {
      paths: 'subset',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  hasActiveSubItem(menuItem: NavigationMenuItem): boolean {
    const isActiveChildren = some(menuItem.subItems, (item) => this.isActive(item.href || ''));
    if (isActiveChildren) {
      menuItem.isCollapsed = false;
    }
    return isActiveChildren;
  }

  toggleCollapsableItem(menuItem: NavigationMenuItem): void {
    menuItem.isCollapsed = !menuItem.isCollapsed;
  }

  openBusinessAccountListModal() {
    this.modalService.open(BusinessAccountListComponent, {
      className: 'business-account-list-modal',
      size: 'full-size',
    });
  }

  onBusinessAccountSelectChange(linkedBusinessAccount: LinkedBusinessAccount): void {
    if (!linkedBusinessAccount) {
      return;
    }

    this.store.dispatch(AuthActions.selectBusinessAccount({ businessAccount: linkedBusinessAccount }));
  }
}
